import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useSubscription } from './useSubscription';
import { logger } from '@/lib/logger';

interface PlanFeature {
  key: string;
  name: string;
  description: string;
  category: string;
  is_enabled: boolean;
}

interface FeatureAccessResult {
  hasAccess: boolean;
  feature?: PlanFeature;
  plan?: {
    name: string;
    slug: string;
  };
  isLoading: boolean;
}

export function useFeatureAccess(featureKey: string): FeatureAccessResult {
  const { user } = useAuth();
  const { subscription, plan } = useSubscription();

  const { data, isLoading } = useQuery({
    queryKey: ['feature-access', user?.id, featureKey, subscription?.id],
    queryFn: async () => {
      if (!user) {
        return { hasAccess: false };
      }

      // 1. Verificar override manual para este proveedor
      const { data: override, error: overrideError } = await supabase
        .from('provider_feature_overrides')
        .select('is_enabled')
        .eq('provider_id', user.id)
        .eq('feature_key', featureKey)
        .maybeSingle();

      if (overrideError && overrideError.code !== 'PGRST116') {
        logger.error('Error checking feature override:', overrideError);
      }

      if (override !== null) {
        const { data: featureInfo } = await supabase
          .from('plan_features')
          .select('key, name, description, category')
          .eq('key', featureKey)
          .maybeSingle();

        return {
          hasAccess: override.is_enabled,
          feature: featureInfo as PlanFeature,
          plan: plan ? { name: plan.name, slug: plan.slug } : undefined,
        };
      }

      // 2. Deny access unless status is 'active' or valid 'trialing'
      const isActiveTrialing =
        subscription?.status === 'trialing' &&
        subscription.trial_ends_at &&
        new Date(subscription.trial_ends_at) > new Date();

      if (subscription?.status !== 'active' && !isActiveTrialing) {
        return { hasAccess: false };
      }

      // 3. Verificar permisos del plan
      if (!subscription?.plan_id) {
        return { hasAccess: false };
      }

      const { data: featureData, error } = await supabase
        .from('plan_feature_assignments')
        .select(`
          is_enabled,
          feature:plan_features(key, name, description, category)
        `)
        .eq('plan_id', subscription.plan_id)
        .eq('feature_key', featureKey)
        .eq('is_enabled', true)
        .maybeSingle();

      if (error) {
        logger.error('Error checking feature access:', error);
        return { hasAccess: false };
      }

      if (!featureData) {
        return { hasAccess: false };
      }

      return {
        hasAccess: true,
        feature: featureData.feature as PlanFeature,
        plan: plan ? { name: plan.name, slug: plan.slug } : undefined,
      };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  return {
    hasAccess: data?.hasAccess || false,
    feature: data?.feature,
    plan: data?.plan,
    isLoading,
  };
}

export function useProviderFeatures() {
  const { user } = useAuth();
  const { subscription, plan } = useSubscription();

  const { data: features, isLoading } = useQuery({
    queryKey: ['provider-features', user?.id, subscription?.plan_id],
    queryFn: async () => {
      if (!user) {
        return [];
      }

      // 1. Obtener overrides del proveedor
      const { data: overrides } = await supabase
        .from('provider_feature_overrides')
        .select(`
          feature_key,
          is_enabled,
          feature:plan_features(
            key,
            name,
            description,
            category,
            is_active
          )
        `)
        .eq('provider_id', user.id)
        .eq('is_enabled', true);

      const overrideFeatures = (overrides || [])
        .map((item) => item.feature)
        .filter((f) => f && f.is_active);

      // 2. Deny plan features unless status is 'active' or valid 'trialing'
      const isActiveTrialing =
        subscription?.status === 'trialing' &&
        subscription.trial_ends_at &&
        new Date(subscription.trial_ends_at) > new Date();

      if (subscription?.status !== 'active' && !isActiveTrialing) {
        return overrideFeatures;
      }

      // 3. Si no hay plan, solo retornar overrides
      if (!subscription?.plan_id) {
        return overrideFeatures;
      }

      // 4. Obtener features del plan
      const { data, error } = await supabase
        .from('plan_feature_assignments')
        .select(`
          feature:plan_features(
            key,
            name,
            description,
            category,
            is_active
          ),
          is_enabled
        `)
        .eq('plan_id', subscription.plan_id)
        .eq('is_enabled', true);

      if (error) {
        logger.error('Error fetching provider features:', error);
        return overrideFeatures;
      }

      const planFeatures = (data || [])
        .map((item) => item.feature)
        .filter((f) => f && f.is_active);

      // 5. Combinar features del plan + overrides (sin duplicados)
      const allFeatures = [...planFeatures, ...overrideFeatures];
      const uniqueFeatures = allFeatures.filter(
        (feature, index, self) =>
          index === self.findIndex((f) => f.key === feature.key)
      );

      return uniqueFeatures;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  return {
    features: features || [],
    plan,
    isLoading,
  };
}

export function useIsSuperAdmin() {
  const { user } = useAuth();

  const { data: isSuperAdmin, isLoading } = useQuery({
    queryKey: ['is-superadmin', user?.id],
    queryFn: async () => {
      if (!user) return false;

      const { data, error } = await supabase
        .from('profiles')
        .select('is_superadmin')
        .eq('id', user.id)
        .single();

      if (error) {
        logger.error('Error checking superadmin status:', error);
        return false;
      }

      return data?.is_superadmin || false;
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
  });

  return {
    isSuperAdmin: isSuperAdmin || false,
    isLoading,
  };
}